import jsQR from "jsqr";

export type QrErrorCode = "no-image" | "unreadable" | "no-code" | "clipboard-denied";

export class QrError extends Error {
  public readonly code: QrErrorCode;

  public constructor(code: QrErrorCode, message: string) {
    super(message);
    this.name = "QrError";
    this.code = code;
  }
}

/** Large screenshots get scaled down; QR codes stay readable and decoding stays fast. */
const MAX_EDGE = 1600;

/** Decodes the first QR code in an image (screenshot, photo, exported PNG). Runs entirely locally. */
export async function decodeQrImage(blob: Blob): Promise<string> {
  if (!blob.type.startsWith("image/")) {
    throw new QrError("no-image", "Die Datei ist kein Bild.");
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    throw new QrError("unreadable", "Das Bild konnte nicht gelesen werden.");
  }
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx === null) {
      throw new QrError("unreadable", "Das Bild konnte nicht gelesen werden.");
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    const pixels = ctx.getImageData(0, 0, width, height);
    const code = jsQR(pixels.data, width, height, { inversionAttempts: "attemptBoth" });
    if (code === null || code.data === "") {
      throw new QrError("no-code", "Im Bild wurde kein QR-Code gefunden.");
    }
    return code.data;
  } finally {
    bitmap.close();
  }
}

/** First image in the clipboard, via the async Clipboard API (needs a user gesture and permission). */
export async function readClipboardImage(): Promise<Blob> {
  let items: ClipboardItems;
  try {
    items = await navigator.clipboard.read();
  } catch {
    throw new QrError("clipboard-denied", "Kein Zugriff auf die Zwischenablage. Bild stattdessen mit Strg + V ins Feld einfügen.");
  }
  for (const item of items) {
    const type = item.types.find((t: string): boolean => t.startsWith("image/"));
    if (type !== undefined) {
      return item.getType(type);
    }
  }
  throw new QrError("no-image", "In der Zwischenablage ist kein Bild.");
}

/** Image from a paste event, if any. */
export function pastedImage(data: DataTransfer | null): File | undefined {
  if (data === null) {
    return undefined;
  }
  for (const file of Array.from(data.files)) {
    if (file.type.startsWith("image/")) {
      return file;
    }
  }
  return undefined;
}
